
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '../ui/card';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { EmailAuthForm } from './EmailAuthForm';
import { GoogleAuthButton } from './GoogleAuthButton';

const AuthForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSignUp, setIsSignUp] = useState(false);
  const navigate = useNavigate();

  const handleError = (message: string) => {
    setError(message);
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        setError(error.message);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    }
    // Remove setLoading(false) from finally block to keep loading state while redirect happens
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) {
        setError(error.message);
      } else {
        // Redirect to onboarding after successful signup
        toast.success('Account created! Redirecting to onboarding...');
        navigate('/onboarding');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card 
      className="mx-auto w-full p-4 sm:w-[400px] sm:p-6"
      data-testid="auth-card"
    >
      <h1 className="text-2xl font-semibold text-center mb-6">
        {isSignUp ? 'Create Account' : 'Welcome Back'}
      </h1>
      {error && (
        <p className="text-red-500 text-sm mb-4" role="alert">
          {error}
        </p>
      )}
      
      <EmailAuthForm
        email={email}
        setEmail={setEmail}
        password={password}
        setPassword={setPassword}
        loading={loading}
        isSignUp={isSignUp}
        onSubmit={isSignUp ? handleSignUp : handleSignIn}
      />
      
      <div className="mt-4 text-center text-sm">
        <button
          type="button"
          onClick={() => {
            setIsSignUp(!isSignUp);
            setError(null); // Clear any previous errors when switching modes
          }}
          className="text-primary hover:underline"
          disabled={loading}
        >
          {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
        </button>
      </div>

      <div className="mt-4 relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">
            Or continue with
          </span>
        </div>
      </div>

      <div className="mt-4">
        <GoogleAuthButton loading={loading} onError={handleError} />
      </div>
    </Card>
  );
};

export default AuthForm;
